/*
 * Cookie-consent store — our own replacement for Cookiebot.
 *
 * The consent decision is persisted in a first-party cookie (`tb_consent`) so
 * that it is readable across every webpack bundle (browser app, ads bootstrap)
 * and, if ever needed, server-side. Because the browser app and the ads
 * bootstrap are SEPARATE bundles (each with its own module instance), in-memory
 * subscriber lists would NOT be shared between them — so change notifications go
 * over the `window` as CustomEvents, which every bundle on the page can hear.
 *
 * Categories:
 *   - analytics : Google Tag Manager + Cloudflare analytics
 *   - ads       : the Freestar / pub.network advertising scripts
 * Strictly-necessary cookies (session, theme, this consent cookie) are always on
 * and are not represented here.
 */

import { adsEnabled } from "./config.ts";

export type ConsentCategories = {
  readonly analytics: boolean;
  readonly ads: boolean;
};

export type ConsentState = ConsentCategories & {
  /** Schema version — bumping it invalidates old decisions and re-prompts. */
  readonly v: number;
  /** Epoch millis when the decision was recorded. */
  readonly ts: number;
};

const COOKIE_NAME = "tb_consent";
const VERSION = 1;
const MAX_AGE_SECONDS = 180 * 24 * 60 * 60; // ~6 months
const CHANGE_EVENT = "tb:consent";
const OPEN_EVENT = "tb:consent-open";

function isBrowser(): boolean {
  return typeof document !== "undefined";
}

/** The recorded decision, or `null` when the user has not decided yet. */
export function getConsent(): ConsentState | null {
  if (!isBrowser()) {
    return null;
  }
  const raw = readCookie(COOKIE_NAME);
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(decodeURIComponent(raw)) as Partial<ConsentState>;
    if (
      parsed != null &&
      parsed.v === VERSION &&
      typeof parsed.analytics === "boolean" &&
      typeof parsed.ads === "boolean"
    ) {
      return parsed as ConsentState;
    }
  } catch {
    // Malformed or stale-version cookie — treat as "no decision".
  }
  return null;
}

export function hasConsentDecision(): boolean {
  return getConsent() != null;
}

/** Convenience: is a single category granted right now? */
export function isGranted(category: keyof ConsentCategories): boolean {
  return getConsent()?.[category] === true;
}

/** Record a decision, persist it, and notify every bundle on the page. */
export function setConsent(categories: ConsentCategories): ConsentState {
  const state: ConsentState = {
    v: VERSION,
    ts: Date.now(),
    analytics: categories.analytics === true,
    ads: categories.ads === true,
  };
  if (isBrowser()) {
    writeCookie(COOKIE_NAME, encodeURIComponent(JSON.stringify(state)));
    window.dispatchEvent(
      new CustomEvent<ConsentState>(CHANGE_EVENT, { detail: state }),
    );
  }
  return state;
}

export function acceptAll(): ConsentState {
  // "Accept all" cannot grant a category the site does not run. With ads off
  // this records `ads: false`, which is the truth and keeps the stored decision
  // consistent with what the page actually loads.
  return setConsent({ analytics: true, ads: adsEnabled });
}

export function rejectAll(): ConsentState {
  return setConsent({ analytics: false, ads: false });
}

/** Subscribe to consent changes (fires on every `setConsent`). */
export function onConsentChange(
  callback: (state: ConsentState) => void,
): () => void {
  if (!isBrowser()) {
    return () => {};
  }
  const handler = (event: Event) => {
    callback((event as CustomEvent<ConsentState>).detail);
  };
  window.addEventListener(CHANGE_EVENT, handler);
  return () => window.removeEventListener(CHANGE_EVENT, handler);
}

/** Ask the banner to reopen (wired to the footer "Cookie settings" link). */
export function openConsentSettings(): void {
  if (isBrowser()) {
    window.dispatchEvent(new CustomEvent(OPEN_EVENT));
  }
}

export function onOpenConsentSettings(callback: () => void): () => void {
  if (!isBrowser()) {
    return () => {};
  }
  const handler = () => callback();
  window.addEventListener(OPEN_EVENT, handler);
  return () => window.removeEventListener(OPEN_EVENT, handler);
}

function readCookie(name: string): string | null {
  const prefix = `${name}=`;
  for (const part of document.cookie.split(";")) {
    const cookie = part.trim();
    if (cookie.startsWith(prefix)) {
      return cookie.slice(prefix.length);
    }
  }
  return null;
}

function writeCookie(name: string, value: string): void {
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie =
    `${name}=${value}; Max-Age=${MAX_AGE_SECONDS}; Path=/; SameSite=Lax` +
    secure;
}
